"use client";
import React from "react";
import { ButtonCta } from "@/components";
import { SectionHeading } from "@/components/common/decor";

const genres = [
  {
    title: "Fiction & Thrillers",
    desc: "Tight pacing, layered plot, and reveals that land.",
    // magnifier — detective / mystery
    icon: (
      <>
        <circle cx="11" cy="11" r="6.5" />
        <path d="M20.5 20.5 16 16" />
      </>
    ),
    cover: "/images/book-covers/hero-slide-13.webp",
  },
  {
    title: "Romance & Drama",
    desc: "Emotional pacing and chemistry that keep pages turning.",
    // heart
    icon: <path d="M12 20s-7-4.35-7-9.5A3.5 3.5 0 0 1 12 8a3.5 3.5 0 0 1 7 2.5C19 15.65 12 20 12 20Z" />,
    cover: "/images/book-covers/hero-slide-6.webp",
  },
  {
    title: "Fantasy & Sci-Fi",
    desc: "Original worldbuilding with consistent internal logic.",
    // ringed planet + spark
    icon: (
      <>
        <circle cx="11" cy="12" r="5" />
        <path d="M4.8 16.8c-1.4 1.4-2.2 2.7-1.8 3.4.6 1.1 4 .1 8.4-2.4s7.4-5.7 6.8-6.8c-.4-.7-1.9-.6-3.9.2" />
        <path d="M19 4.5v2.4M17.8 5.7h2.4" />
      </>
    ),
    cover: "/images/book-covers/hero-slide-8.webp",
  },
  {
    title: "Memoir & Biography",
    desc: "Personal stories told with accuracy and heart.",
    // portrait in frame
    icon: (
      <>
        <rect x="4" y="3" width="16" height="18" rx="2.5" />
        <circle cx="12" cy="10" r="2.6" />
        <path d="M7.5 17.5a4.5 4.5 0 0 1 9 0" />
      </>
    ),
    cover: "/images/book-covers/hero-slide-3.webp",
  },
  {
    title: "Business & Leadership",
    desc: "Clear, structured non-fiction for professional readers.",
    // briefcase
    icon: (
      <>
        <rect x="3" y="7.5" width="18" height="12" rx="2.5" />
        <path d="M8.5 7.5V6a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v1.5" />
        <path d="M3 12.5h18" />
      </>
    ),
    cover: "/images/Item-2.webp",
  },
  {
    title: "Self-Help & Wellbeing",
    desc: "Practical, goal-driven content readers can act on.",
    // leaf
    icon: (
      <>
        <path d="M4 20c0-8 6-13.5 16-13.5C20 16 14 20 6 20" />
        <path d="M7.5 16.5c2.2-4 5-6.2 8.5-7.5" />
      </>
    ),
    cover: "/images/book-covers/hero-slide-7.webp",
  },
  {
    title: "Children's Books",
    desc: "Age-appropriate storytelling paired with original art.",
    // balloon
    icon: (
      <>
        <path d="M12 15c3 0 5.5-2.7 5.5-6S15 3 12 3 6.5 5.7 6.5 9 9 15 12 15Z" />
        <path d="M12 15v1.5" />
        <path d="M12 16.5c0 2 1.6 2.3 1.6 4.5M12 16.5c0 2-1.6 2.3-1.6 4.5" />
      </>
    ),
    cover: "/images/book-covers/hero-slide-10.webp",
  },
  {
    title: "Poetry & Anthologies",
    desc: "Collections designed with intention for print and digital.",
    // sprout / growth
    icon: (
      <>
        <path d="M12 21v-9" />
        <path d="M12 14c-2.5 0-4.5-1.8-4.5-4.5C10 9.5 12 11.3 12 14Z" />
        <path d="M12 12.5c0-2.7 2-4.5 4.5-4.5C16.5 10.7 14.5 12.5 12 12.5Z" />
      </>
    ),
    cover: "/images/Item-6.webp",
  },
  {
    title: "Inspirational & Spiritual",
    desc: "Reflective, theme-driven writing with a clear arc.",
    // sunrise
    icon: (
      <>
        <path d="M3.5 18.5h17" />
        <path d="M7 18.5a5 5 0 0 1 10 0" />
        <path d="M12 5v2.5M5.5 8l1.5 1.5M18.5 8 17 9.5M3.5 14h1.5M19 14h1.5" />
      </>
    ),
    cover: "/images/book-covers/hero-slide-1.webp",
  },
];

export default function ProfessionalGenres() {
  return (
    <section className="relative overflow-hidden bg-paper py-20">
      <div className="mx-auto max-w-6xl px-4">
        <SectionHeading
          eyebrow="Genre Expertise"
          title="Whatever You Write, We Speak Your Genre"
          description="Every manuscript is matched with a writer and editor fluent in the conventions and pacing readers expect from your category. Hover any genre below to see a title we've shaped."
          className="mb-14"
        />

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {genres.map((g, i) => (
            <article
              key={g.title}
              data-aos="fade-up"
              data-aos-delay={(i % 3) * 90}
              className="group relative h-52 overflow-hidden rounded-3xl border border-ink/10 bg-white"
            >
              {/* cover reveal */}
              <img loading="lazy" decoding="async"
                src={g.cover}
                alt=""
                aria-hidden
                className="absolute inset-0 h-full w-full scale-105 object-cover opacity-0 transition-all duration-700 group-hover:scale-100 group-hover:opacity-100"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/50 to-ink/10 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

              {/* content */}
              <div className="relative flex h-full flex-col p-7">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand-700 transition-colors duration-500 group-hover:bg-white/15 group-hover:text-white">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-6 w-6"
                  >
                    {g.icon}
                  </svg>
                </span>

                <h3 className="mt-auto font-display text-xl font-semibold text-ink transition-colors duration-500 group-hover:text-white">
                  {g.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-ink-soft transition-colors duration-500 group-hover:text-white/80">
                  {g.desc}
                </p>

                {/* gold hairline */}
                <span className="mt-3 block h-px w-0 bg-gold transition-all duration-500 group-hover:w-14" />
              </div>
            </article>
          ))}
        </div>

        <div className="mt-12 flex items-center justify-center">
          <ButtonCta buttonText="Request a Free Quote" classStyle="text-white bg-brand-600 hover:bg-brand-700 h-12" />
        </div>
      </div>
    </section>
  );
}
